const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
// Google ad click identifiers. gclid = standard, gbraid/wbraid = iOS/privacy variants.
const CLICK_ID_PARAMS = ['gclid', 'gbraid', 'wbraid'] as const;
const UTM_STORAGE_KEY = 'utm_data';
const MAX_UTM_LENGTH = 100;

function readCookie(name: string): string | undefined {
  const row = document.cookie.split('; ').find(r => r.startsWith(name + '='));
  if (!row) return undefined;
  try {
    return decodeURIComponent(row.slice(name.length + 1));
  } catch {
    return undefined; // malformed percent-encoding
  }
}

function parseJsonObject(raw: string | undefined | null): Record<string, string> | null {
  if (!raw) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (value && typeof value === 'object') return value as Record<string, string>;
  } catch { /* ignore malformed */ }
  return null;
}

function readUtmFromUrl(): Record<string, string> | null {
  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};
  for (const key of UTM_PARAMS) {
    const value = params.get(key)?.trim().slice(0, MAX_UTM_LENGTH);
    if (value) utm[key] = value;
  }
  return Object.keys(utm).length > 0 ? utm : null;
}

/**
 * Extract the click id from a `_gcl_*` cookie set by Google's Conversion Linker.
 * Format: `GCL.<timestamp>.<clickId>` — the clickId may itself contain dots, so take
 * everything after the second segment.
 */
function parseGclCookie(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const parts = raw.split('.');
  if (parts[0] !== 'GCL' || parts.length < 3) return undefined;
  const id = parts.slice(2).join('.').trim().slice(0, MAX_UTM_LENGTH);
  return id || undefined;
}

/**
 * Read Google ad click ids. Primary source is the `_gcl_*` cookie, which the marketing
 * site's Conversion Linker sets on the parent domain (`.optidev.ai`) and is therefore
 * readable on `app.optidev.ai` even after the SSO redirect strips the URL. URL params
 * are a fallback for direct app links.
 */
function readGoogleClickIds(): Record<string, string> {
  const out: Record<string, string> = {};

  const gclid = parseGclCookie(readCookie('_gcl_aw'));
  if (gclid) out.gclid = gclid;
  const gbraid = parseGclCookie(readCookie('_gcl_gb'));
  if (gbraid) out.gbraid = gbraid;

  const params = new URLSearchParams(window.location.search);
  for (const key of CLICK_ID_PARAMS) {
    if (out[key]) continue;
    const value = params.get(key)?.trim().slice(0, MAX_UTM_LENGTH);
    if (value) out[key] = value;
  }
  return out;
}

/**
 * Capture UTM + Google click id data from all available sources and persist.
 * MUST be called before any OAuth redirect.
 * All operations are synchronous (localStorage + cookie) - no network calls.
 */
export function captureAndPersistUtm(): Record<string, string> | null {
  // UTM precedence: URL (freshest) → cross-domain cookie → localStorage fallback.
  const utm = readUtmFromUrl()
    ?? parseJsonObject(readCookie(UTM_STORAGE_KEY))
    ?? parseJsonObject(localStorage.getItem(UTM_STORAGE_KEY))
    ?? {};

  const merged = { ...utm, ...readGoogleClickIds() };
  if (Object.keys(merged).length === 0) return null;

  localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(merged));
  return merged;
}

/**
 * Get persisted attribution data (post-OAuth). Merges stored UTM with a fresh read of
 * the `_gcl_*` cookies, so a click id set on the parent domain is picked up even when
 * localStorage was never written (e.g. SSO redirect fired before capture ran).
 */
export function getPersistedUtm(): Record<string, string> | null {
  const stored = parseJsonObject(localStorage.getItem(UTM_STORAGE_KEY))
    ?? parseJsonObject(readCookie(UTM_STORAGE_KEY))
    ?? {};

  const merged = { ...stored, ...readGoogleClickIds() };
  return Object.keys(merged).length > 0 ? merged : null;
}

export function clearUtmData(): void {
  localStorage.removeItem(UTM_STORAGE_KEY);

  // The marketing site sets the cookie with Domain=.optiedge.cc or Domain=.optidev.ai.
  // To clear it, the Domain must match exactly how it was set.
  const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
  const parts = window.location.hostname.split('.');
  const parentDomain = parts.slice(-2).join('.');

  document.cookie = `utm_data=; ${expiry}; path=/; domain=.${parentDomain}`;
  document.cookie = `utm_data=; ${expiry}; path=/`;
}
